import { useState, useMemo, useRef } from "react";
import Spinner from "../../../globalComponents/ui/Spinner";
import KpiPickerList from "./KpiPickerList";
import { useSaveMappings } from "./useSaveMappings";
import { assetScopeLabel, buildMapping, kpiAssetKind } from "./helpers";

/**
 * React 16 has no useId — a module counter keeps label/control ids unique
 * when several panels are mounted at once.
 */
let panelInstanceCounter = 0;

/**
 * Add-KPIs panel (asset view): pick KPIs, attach this asset to them.
 *
 * The asset (title + scope + unit) is fixed by the group; the user searches
 * and ticks KPIs, and one bulk save creates the missing mappings. KPIs whose
 * existing assets are locked to the other scope can't receive it.
 */
export default function AddKpisToAssetPanel({
  group,
  allQuestions,
  kpiAssetMap,
  sources,
  selectedFYId,
  onSaved,
  onClose,
}) {
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = `kpa-addkpis-${++panelInstanceCounter}`;

  const { asset, members } = group;
  const assetKind = asset.isLocationRequired ? "location" : "global";
  const { hasLocation, unitLabel } = assetScopeLabel(asset, sources);

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const { saving, saveError, save } = useSaveMappings(selectedFYId, onSaved, onClose);

  /**
   * KPIs not yet holding this asset. Same title + scope + unit would make
   * them members, so no separate duplicate check is needed here.
   */
  const candidates = useMemo(() => {
    const memberKpiIds = new Set(members.map(m => String(m.kpiId)));
    return allQuestions
      .filter(q => !memberKpiIds.has(String(q.questionId)))
      .map(q => {
        const kind = kpiAssetKind(kpiAssetMap[q.questionId] || []);
        return {
          kpiId: q.questionId,
          kpiTitle: q.title,
          tags: q.tags,
          locked: !!kind && kind !== assetKind,
          lockedLabel: assetKind === "location" ? "global only" : "location only",
        };
      });
  }, [allQuestions, kpiAssetMap, members, assetKind]);

  const toggleKpi = (kpiId) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(kpiId)) next.delete(kpiId); else next.add(kpiId);
    return next;
  });

  const handleSave = () => {
    save([...selectedIds].map(kpiId => buildMapping({
      kpiId,
      assetTitle: asset.assetTitle,
      isLocationRequired: asset.isLocationRequired,
      sourceId: asset.sourceId,
      subLocationId: asset.subLocationId,
    })));
  };

  return (
    <div className="kpa-panel" style={{ marginTop: 12 }}>
      <div className="kpa-panel-header">
        <span className="kpa-panel-title">Add “{asset.assetTitle}” to more KPIs</span>
        <button type="button" className="kpa-panel-close" onClick={onClose} aria-label="Close add panel">✕</button>
      </div>

      <div className="kpa-panel-body">
        <div className={`kpa-unit-pill kpa-unit-pill--${assetKind}`}>
          <i className={`fas ${hasLocation ? "fa-map-marker-alt" : "fa-globe"}`} aria-hidden="true" />
          <span>{asset.isLocationRequired ? unitLabel : "Global"}</span>
        </div>

        <KpiPickerList
          candidates={candidates}
          selectedIds={selectedIds}
          onToggle={toggleKpi}
          disabled={saving}
          idBase={idRef.current}
          emptyText="All KPIs already have this asset."
        />

        {saveError && (
          <div className="kpa-error-box" role="alert">
            <i className="fas fa-exclamation-circle" aria-hidden="true" />
            <span>{saveError}</span>
          </div>
        )}

        <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid var(--kpa-slate-100)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <div className={`kpa-selection-strip${selectedIds.size ? " kpa-selection-strip--ready" : ""}`} style={{ margin: 0 }} role="status">
            <i className={`fas ${selectedIds.size ? "fa-check-square" : "fa-hand-pointer"}`} aria-hidden="true" />
            <span>
              {selectedIds.size
                ? `${selectedIds.size} KPI${selectedIds.size !== 1 ? "s" : ""} selected`
                : "Tick the KPIs to add this asset to."}
            </span>
          </div>
          <button
            type="button"
            className="kpa-btn kpa-btn--primary"
            onClick={handleSave}
            disabled={saving || !selectedIds.size || !selectedFYId}
          >
            {saving
              ? <><Spinner size={11} color="#fff" style={{ marginRight: 6 }} />Saving…</>
              : <>
                <i className="fas fa-save" style={{ marginRight: 5 }} aria-hidden="true" />
                Add to {selectedIds.size || ""} KPI{selectedIds.size !== 1 ? "s" : ""}
              </>
            }
          </button>
        </div>
      </div>
    </div>
  );
}
